import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Drawer,
  Group,
  Menu,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconDots,
  IconPlus,
  IconStar,
  IconTrash,
} from "@tabler/icons-react";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Can } from "@/components/Can";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import type {
  Driver,
  DriversListResponse,
  Vehicle,
  VehicleDriver,
  VehicleDriversResponse,
} from "@/types/api";

interface AddDriverFormValues {
  driver_id: string;
  is_primary: boolean;
}

export function VehicleDrivers({
  vehicle,
  opened,
  onClose,
}: {
  vehicle: Vehicle;
  opened: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const { data: driversData, isLoading } = useQuery({
    queryKey: qk.vehicleDrivers(vehicle.id),
    queryFn: async () =>
      (await api.get<VehicleDriversResponse>(`/api/v1/vehicles/${vehicle.id}/drivers`)).data,
    enabled: opened,
  });
  const drivers = driversData?.items ?? [];

  // Load all drivers for the add-driver select (filter by carrier if vehicle has one)
  const driverParams = vehicle.carrier_id
    ? `/api/v1/drivers?page=1&page_size=200&carrier_id=${vehicle.carrier_id}`
    : "/api/v1/drivers?page=1&page_size=200";

  const { data: allDriversData } = useQuery({
    queryKey: qk.drivers({ page: 1, page_size: 200, carrier_id: vehicle.carrier_id }),
    queryFn: async () =>
      (await api.get<DriversListResponse>(driverParams)).data,
    enabled: opened,
  });

  const assignedIds = new Set(drivers.map((d) => d.driver_id));
  const availableDrivers = (allDriversData?.items ?? []).filter(
    (d: Driver) => !assignedIds.has(d.id),
  );
  const driverOptions = availableDrivers.map((d: Driver) => ({
    value: d.id,
    label: d.full_name,
  }));

  const [addOpened, setAddOpened] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<VehicleDriver | null>(null);

  const form = useForm<AddDriverFormValues>({
    initialValues: { driver_id: "", is_primary: false },
    validate: {
      driver_id: (v) => (v ? null : "Выберите водителя"),
    },
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["vehicleDrivers", vehicle.id] });

  const addMut = useMutation({
    mutationFn: async (body: AddDriverFormValues) =>
      (await api.post(`/api/v1/vehicles/${vehicle.id}/drivers`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Водитель добавлен", color: "green" });
      invalidate();
      setAddOpened(false);
      form.reset();
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const removeMut = useMutation({
    mutationFn: async (driverId: string) =>
      (await api.delete(`/api/v1/vehicles/${vehicle.id}/drivers/${driverId}`)).data,
    onSuccess: () => {
      notifications.show({ message: "Водитель откреплён", color: "green" });
      invalidate();
      setConfirmRemove(null);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const primaryMut = useMutation({
    mutationFn: async (driverId: string) =>
      (await api.post(`/api/v1/vehicles/${vehicle.id}/drivers/${driverId}/primary`)).data,
    onSuccess: () => {
      notifications.show({ message: "Основной водитель установлен", color: "green" });
      invalidate();
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const submit = () => {
    if (form.validate().hasErrors) return;
    addMut.mutate(form.values);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <div>
          <Text fw={600}>Водители</Text>
          <Text size="sm" c="dimmed">
            {vehicle.brand}{vehicle.model ? ` ${vehicle.model}` : ""} — {vehicle.license_plate}
          </Text>
        </div>
      }
      position="right"
      size="lg"
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>Назначенные водители</Title>
          <Can perm="vehicles:write">
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                form.reset();
                setAddOpened(true);
              }}
            >
              Добавить
            </Button>
          </Can>
        </Group>

        {isLoading && <Text c="dimmed" size="sm">Загрузка...</Text>}

        {!isLoading && drivers.length === 0 && (
          <Text c="dimmed" size="sm">Водители не назначены</Text>
        )}

        {drivers.map((d) => (
          <Stack
            key={d.driver_id}
            gap={4}
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-2)",
              paddingBottom: 12,
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Text fw={500}>{d.driver_full_name}</Text>
                {d.is_primary && (
                  <Badge color="blue" size="xs">
                    Основной
                  </Badge>
                )}
              </Group>
              <Can perm="vehicles:write">
                <Menu position="bottom-end" shadow="md">
                  <Menu.Target>
                    <ActionIcon variant="subtle" size="sm">
                      <IconDots size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {!d.is_primary && (
                      <Menu.Item
                        leftSection={<IconStar size={14} />}
                        onClick={() => primaryMut.mutate(d.driver_id)}
                      >
                        Сделать основным
                      </Menu.Item>
                    )}
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => setConfirmRemove(d)}
                    >
                      Открепить
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Can>
            </Group>
          </Stack>
        ))}
      </Stack>

      <FormModal
        opened={addOpened}
        onClose={() => setAddOpened(false)}
        title="Добавить водителя"
        onSubmit={submit}
        loading={addMut.isPending}
      >
        <Stack gap="md">
          <Select
            label="Водитель"
            placeholder="Выберите водителя"
            data={driverOptions}
            searchable
            required
            {...form.getInputProps("driver_id")}
          />
        </Stack>
      </FormModal>

      <ConfirmDialog
        opened={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => confirmRemove && removeMut.mutate(confirmRemove.driver_id)}
        title="Открепить водителя?"
        message={`Водитель "${confirmRemove?.driver_full_name}" будет откреплён от ТС.`}
        loading={removeMut.isPending}
      />
    </Drawer>
  );
}
